import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { handleApiError, handleApiSuccess } from '../components/apiErrorHandler';
import axiosInstance from '../services/axiosInstance';

const PAYMENT_METHODS = [
    { label: 'Cash',        value: 'cash',    icon: 'pi-wallet'       },
    { label: 'Card',        value: 'card',    icon: 'pi-credit-card'  },
    { label: 'Mobile',      value: 'mobile',  icon: 'pi-mobile'       },
    { label: 'Account Pay', value: 'account', icon: 'pi-building'     },
];

const fmt = (val) =>
    new Intl.NumberFormat('en-LK', { style: 'currency', currency: 'LKR' }).format(val || 0);

const PaymentDialog = ({ visible, onHide, invoice, invoiceOperator, onPaymentSaved }) => {
    const balanceDue = (invoice?.net_price || 0) - (invoice?.paid_amount || 0);

    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [paymentAmount, setPaymentAmount] = useState(balanceDue);
    const [paymentNotes,  setPaymentNotes]  = useState('');
    const [cashAmount,    setCashAmount]    = useState(0);
    const [balance,       setBalance]       = useState(0);

    const cashAmountRef    = useRef(null);
    const paymentAmountRef = useRef(null);
    const submitBtnRef     = useRef(null);

    const focusPrimary = useCallback(() => {
        // Retry focus until the input is ready — handles PrimeReact mount delay
        let attempts = 0;
        const tryFocus = () => {
            const el = paymentMethod === 'cash'
                ? cashAmountRef.current?.getInput?.() || cashAmountRef.current
                : paymentAmountRef.current?.getInput?.() || paymentAmountRef.current;
            if (el) {
                el.focus();
                el.select?.();
            } else if (attempts++ < 10) {
                setTimeout(tryFocus, 50);
            }
        };
        setTimeout(tryFocus, 80);
    }, [paymentMethod]);

    // Reset state and focus when dialog opens
    useEffect(() => {
        if (!visible) return;
        const due = (invoice?.net_price || 0) - (invoice?.paid_amount || 0);
        setPaymentMethod('cash');
        setPaymentAmount(due);
        setPaymentNotes('');
        setCashAmount(0);
        setBalance(0);
    }, [visible]);

    // Focus after reset settles
    useEffect(() => {
        if (visible) focusPrimary();
    }, [visible, paymentMethod]);

    // Keep paymentAmount in sync with cash tendered
    useEffect(() => {
        const due = (invoice?.net_price || 0) - (invoice?.paid_amount || 0);
        if (paymentMethod === 'cash') {
            setPaymentAmount(cashAmount < due ? cashAmount : due);
        } else {
            setPaymentAmount(due);
        }
    }, [cashAmount, paymentMethod, invoice?.net_price]);

    // Keep balance in sync
    useEffect(() => {
        const due = (invoice?.net_price || 0) - (invoice?.paid_amount || 0);
        setBalance((cashAmount || 0) - due);
    }, [paymentAmount, cashAmount, invoice?.net_price]);

    const handleSubmit = async () => {
        const due = (invoice?.net_price || 0) - (invoice?.paid_amount || 0);
        const finalAmount = paymentMethod === 'cash'
            ? (cashAmount < due ? cashAmount : due)
            : paymentAmount;

        try {
            const { data } = await axiosInstance.post(
                `/api/invoices/${invoice.id}/record-payment/`,
                {
                    amount:      finalAmount,
                    method:      paymentMethod,
                    cash_amount: cashAmount,
                    notes:       paymentNotes,
                    operator_id: invoiceOperator?.operator_id || null,
                    operator_verification_method: invoiceOperator?.verification_method || null,
                }
            );
            handleApiSuccess('Payment recorded');
            onPaymentSaved(data);
            onHide();
        } catch (err) {
            handleApiError(err);
        }
    };

    const isCash     = paymentMethod === 'cash';
    const changePositive = balance >= 0;
    const isFullyPaid    = paymentAmount >= balanceDue;

    // ── Method option template ────────────────────────────────────────────
    const methodTemplate = (opt) => opt ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className={`pi ${opt.icon}`} style={{ fontSize: 13 }} />
            <span>{opt.label}</span>
        </div>
    ) : null;

    return (
        <Dialog
            header={null}
            visible={visible}
            modal
            onHide={onHide}
            closable={false}
            style={{ width: '26rem', borderRadius: 14, overflow: 'hidden', padding: 0 }}
            contentStyle={{ padding: 0 }}
        >
            {/* ── Header ─────────────────────────────────────────────────── */}
            <div style={{
                background: 'linear-gradient(135deg, #1e3a5f 0%, #2563a8 100%)',
                padding: '20px 24px 18px',
                color: 'white',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                            width: 36, height: 36, borderRadius: 10,
                            background: 'rgba(255,255,255,0.15)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <i className="pi pi-receipt" style={{ fontSize: 16 }} />
                        </div>
                        <div>
                            <div style={{ fontWeight: 700, fontSize: 15, letterSpacing: '0.1px' }}>
                                Record Payment
                            </div>
                            <div style={{ fontSize: 11.5, opacity: 0.75, marginTop: 1 }}>
                                Invoice #{invoice?.invoice_number || invoice?.id}
                            </div>
                        </div>
                    </div>
                    <button onClick={onHide} style={{
                        background: 'rgba(255,255,255,0.12)', border: 'none',
                        borderRadius: 8, width: 30, height: 30, cursor: 'pointer',
                        color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 13, transition: 'background 0.15s',
                    }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.22)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
                    >
                        <i className="pi pi-times" />
                    </button>
                </div>

                {/* Balance due pill */}
                <div style={{
                    marginTop: 16,
                    background: 'rgba(255,255,255,0.12)',
                    borderRadius: 10, padding: '10px 14px',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                    <span style={{ fontSize: 12, opacity: 0.8 }}>Balance Due</span>
                    <span style={{ fontWeight: 700, fontSize: 18, letterSpacing: '-0.3px' }}>
                        {fmt(balanceDue)}
                    </span>
                </div>
            </div>

            {/* ── Body ───────────────────────────────────────────────────── */}
            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>

                {/* Payment method */}
                <div>
                    <label style={labelStyle}>Payment Method</label>
                    <Dropdown
                        value={paymentMethod}
                        options={PAYMENT_METHODS}
                        onChange={e => setPaymentMethod(e.value)}
                        itemTemplate={methodTemplate}
                        valueTemplate={methodTemplate}
                        style={inputStyle}
                    />
                </div>

                {/* Cash tendered — primary field for cash */}
                {isCash && (
                    <div>
                        <label style={labelStyle}>Cash Tendered</label>
                        <InputNumber
                            inputRef={cashAmountRef}
                            value={cashAmount}
                            onValueChange={e => setCashAmount(e.value)}
                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submitBtnRef.current?.focus?.(); }}}
                            mode="currency"
                            currency="LKR"
                            inputStyle={{ ...inputStyle, fontWeight: 600, fontSize: 15 }}
                        />
                    </div>
                )}

                {/* Payment amount — editable for non-cash */}
                {!isCash && (
                    <div>
                        <label style={labelStyle}>Payment Amount</label>
                        <InputNumber
                            inputRef={paymentAmountRef}
                            value={paymentAmount}
                            onValueChange={e => setPaymentAmount(e.value)}
                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submitBtnRef.current?.focus?.(); }}}
                            mode="currency"
                            currency="LKR"
                            max={balanceDue}
                            min={0}
                            inputStyle={{ ...inputStyle, fontWeight: 600, fontSize: 15 }}
                        />
                    </div>
                )}

                {/* Cash: payment amount (read-only) + change */}
                {isCash && (
                    <div style={{ display: 'flex', gap: 10 }}>
                        <div style={{ flex: 1 }}>
                            <label style={labelStyle}>Amount Applied</label>
                            <InputNumber
                                value={paymentAmount}
                                mode="currency" currency="LKR"
                                readOnly
                                inputStyle={{ ...inputStyle, background: '#f8fafc', color: '#64748b', fontSize: 13 }}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <label style={labelStyle}>Change</label>
                            <div style={{
                                ...inputStyle,
                                height: 40,
                                display: 'flex', alignItems: 'center',
                                background: changePositive ? '#f0fdf4' : '#fef2f2',
                                border: `1.5px solid ${changePositive ? '#bbf7d0' : '#fecaca'}`,
                                color: changePositive ? '#15803d' : '#dc2626',
                                fontWeight: 700, fontSize: 14, paddingLeft: 12,
                                borderRadius: 8,
                            }}>
                                {fmt(Math.max(0, balance))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Notes */}
                <div>
                    <label style={labelStyle}>Notes <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
                    <input
                        value={paymentNotes}
                        onChange={e => setPaymentNotes(e.target.value)}
                        placeholder="e.g. cheque no., reference…"
                        style={{
                            ...inputStyle, width: '100%', height: 36,
                            border: '1.5px solid #e2e8f0', background: 'white',
                            color: '#1e293b', fontFamily: 'inherit', fontSize: 13,
                            paddingLeft: 10, outline: 'none', boxSizing: 'border-box',
                        }}
                        onFocus={e => e.target.style.borderColor = '#2563a8'}
                        onBlur={e  => e.target.style.borderColor = '#e2e8f0'}
                    />
                </div>

                {/* Partial payment notice */}
                {!isFullyPaid && paymentAmount > 0 && (
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        background: '#fffbeb', border: '1px solid #fde68a',
                        borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#92400e',
                    }}>
                        <i className="pi pi-info-circle" style={{ fontSize: 12, flexShrink: 0 }} />
                        Partial payment — {fmt(balanceDue - paymentAmount)} will remain outstanding
                    </div>
                )}
            </div>

            {/* ── Footer ─────────────────────────────────────────────────── */}
            <div style={{
                padding: '12px 24px 20px',
                display: 'flex', gap: 10, justifyContent: 'flex-end',
                borderTop: '1px solid #f1f5f9',
            }}>
                <Button
                    label="Cancel"
                    icon="pi pi-times"
                    outlined
                    onClick={onHide}
                    style={{ borderRadius: 8, fontSize: 13, padding: '8px 16px', border: '1.5px solid #e2e8f0', color: '#64748b' }}
                />
                <Button
                    ref={submitBtnRef}
                    label={isFullyPaid ? 'Confirm Payment' : 'Record Partial'}
                    icon="pi pi-check"
                    onClick={handleSubmit}
                    style={{
                        borderRadius: 8, fontSize: 13, padding: '8px 20px', fontWeight: 600,
                        background: 'linear-gradient(135deg, #1e3a5f 0%, #2563a8 100%)',
                        border: 'none',
                    }}
                />
            </div>
        </Dialog>
    );
};

// ── Shared styles ─────────────────────────────────────────────────────────────
const labelStyle = {
    display: 'block', fontSize: 12, fontWeight: 600,
    color: '#475569', marginBottom: 5, letterSpacing: '0.2px',
};
const inputStyle = {
    borderRadius: 8, width: '100%',
};

export default PaymentDialog;